import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Car, Plus, Search, Calendar, MapPin, DollarSign, 
  MoreVertical, FileText, Route
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const Mileage = () => {
  const { token, loading: authLoading } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    distance_km: 0,
    rate_per_km: 0.67,
    vehicle: '',
    start_location: '',
    end_location: '',
    is_billable: false,
    notes: ''
  });

  useEffect(() => {
    if (!authLoading && token) {
      fetchEntries();
    } else if (!authLoading && !token) {
      setLoading(false);
    }
  }, [token, authLoading]);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/mileage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to fetch mileage entries');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/mileage`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mileage entry added');
      setIsDialogOpen(false);
      resetForm();
      fetchEntries();
    } catch (error) {
      toast.error('Error adding entry');
    }
  };

  const handleConvertToExpense = async (entryId) => {
    try {
      await axios.post(`${API}/mileage/${entryId}/convert-to-expense`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Converted to expense');
      fetchEntries();
    } catch (error) {
      toast.error('Error converting');
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      description: '',
      distance_km: 0,
      rate_per_km: 0.67,
      vehicle: '',
      start_location: '',
      end_location: '',
      is_billable: false,
      notes: ''
    });
  };

  const totalKm = entries.reduce((sum, e) => sum + (e.distance_km || 0), 0);
  const totalAmount = entries.reduce((sum, e) => sum + (e.amount || 0), 0);
  const unexpensed = entries.filter(e => !e.expensed).reduce((sum, e) => sum + (e.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Mileage Tracking</h1>
          <p className="text-slate-400">Track business travel for reimbursement</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Trip
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">Log Mileage</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Date *</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Distance (km) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.distance_km}
                    onChange={(e) => setFormData({...formData, distance_km: parseFloat(e.target.value) || 0})}
                    className="bg-slate-800 border-slate-700 text-white"
                    required
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Description *</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="e.g., Client meeting"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">From</Label>
                  <Input
                    value={formData.start_location}
                    onChange={(e) => setFormData({...formData, start_location: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="Start location"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">To</Label>
                  <Input
                    value={formData.end_location}
                    onChange={(e) => setFormData({...formData, end_location: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="End location"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Vehicle</Label>
                  <Input
                    value={formData.vehicle}
                    onChange={(e) => setFormData({...formData, vehicle: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="e.g., Company Car"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Rate (MYR/km)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.rate_per_km}
                    onChange={(e) => setFormData({...formData, rate_per_km: parseFloat(e.target.value) || 0.67})}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_billable}
                  onChange={(e) => setFormData({...formData, is_billable: e.target.checked})}
                  className="rounded border-slate-700"
                />
                <Label className="text-slate-300">Billable to client</Label>
              </div>
              <div className="p-3 bg-slate-800 rounded-lg">
                <p className="text-slate-300">
                  Estimated Reimbursement: <strong className="text-white">MYR {(formData.distance_km * formData.rate_per_km).toFixed(2)}</strong>
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                  Add Entry
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <Route className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Distance</p>
                <p className="text-xl font-bold text-white">{totalKm.toFixed(1)} km</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Amount</p>
                <p className="text-xl font-bold text-white">MYR {totalAmount.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <FileText className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Pending Expense</p>
                <p className="text-xl font-bold text-white">MYR {unexpensed.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="text-center py-10">
          <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
        </div>
      ) : entries.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-10 text-center">
            <Car className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">No mileage entries</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => (
            <motion.div
              key={entry.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${entry.expensed ? 'bg-emerald-500/20' : 'bg-indigo-500/20'}`}>
                        <Car className={`w-6 h-6 ${entry.expensed ? 'text-emerald-400' : 'text-indigo-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-white">{entry.description}</h3>
                          {entry.expensed && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-400">
                              Expensed
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {entry.date}
                          </span>
                          {entry.start_location && entry.end_location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {entry.start_location} → {entry.end_location}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-white">{entry.distance_km} km</p>
                        <p className="text-sm text-emerald-400">MYR {entry.amount?.toFixed(2)}</p>
                      </div>
                      {!entry.expensed && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4 text-slate-400" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-slate-800 border-slate-700">
                            <DropdownMenuItem 
                              className="text-slate-300"
                              onClick={() => handleConvertToExpense(entry.id)}
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              Convert to Expense
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Mileage;
